import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import type { ApiError } from '@/hooks/mutations/auth';
import { isNoResponse } from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type { GetNPSCampaignQueryKey, GetNPSCampaignResponse } from './types';

const fetchNPSCampaign = async (
  userId: string,
): Promise<GetNPSCampaignResponse> => {
  try {
    const { data } = await apiClient.get<GetNPSCampaignResponse>(
      '/v1/surveys/campaign/customernps',
      { params: { userId } },
    );
    return data;
  } catch (error: unknown) {
    const log = isNoResponse(error) ? Logger.warn : Logger.error;
    log('Error fetching NPS campaign', { error });
    // Parse error response from API
    const apiError: ApiError = {
      message:
        error instanceof Error ? error.message : 'Failed to fetch NPS campaign',
      code:
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof error.code === 'string'
          ? error.code
          : 'NPS_CAMPAIGN_ERROR',
      details:
        typeof error === 'object' &&
        error !== null &&
        'details' in error &&
        error.details !== undefined
          ? error.details
          : undefined,
    };
    throw apiError;
  }
};

export const getNPSCampaignQueryOptions = (userId: string) =>
  queryOptions<
    GetNPSCampaignResponse,
    ApiError,
    GetNPSCampaignResponse,
    GetNPSCampaignQueryKey
  >({
    queryKey: ['surveys', 'campaign', 'customernps', userId],
    queryFn: () => fetchNPSCampaign(userId),
    staleTime: 5 * 60 * 1000, // cache for 5 min — campaign eligibility is stable within a session
  });
